// QR Code (ISO/IEC 18004) encoder — a TypeScript port of the repository's own apps/website/theme/vero/inc/qr.php (byte
// mode, error correction level M, versions 1–10, all eight masks scored with the standard penalty rules). No package,
// no network. Used by the phone-calendar subscription screen. "QR Code" is a registered trademark of DENSO WAVE INCORPORATED.
//
// `decodeQr` reads a matrix made by this encoder back to its text (format bits → mask → codewords → byte segment). It is
// not a general scanner (no image processing, no error correction): it lets the tests prove the code carries exactly
// the given text.

/** level M: [ec codewords per block, [[blocks, data codewords per block], ...]] for versions 1..10 */
const M: Record<number, [number, [number, number][]]> = { 1: [10, [[1, 16]]], 2: [16, [[1, 28]]], 3: [26, [[1, 44]]], 4: [18, [[2, 32]]], 5: [24, [[2, 43]]],
  6: [16, [[4, 27]]], 7: [18, [[4, 31]]], 8: [22, [[2, 38], [2, 39]]], 9: [22, [[3, 36], [2, 37]]], 10: [26, [[4, 43], [1, 44]]] };
const ALIGN: Record<number, number[]> = { 1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50] };

const EXP = new Array<number>(512).fill(0), LOG = new Array<number>(256).fill(0);
{ let x = 1; for (let i = 0; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 0x100) x ^= 0x11d; } for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!; }
const mul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!);

function rsRemainder(data: number[], n: number): number[] {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const out = new Array<number>(g.length + 1).fill(0);
    g.forEach((c, j) => { out[j]! ^= c; out[j + 1]! ^= mul(c, EXP[i]!); });
    g = out;
  }
  const rem = [...data, ...new Array<number>(n).fill(0)];
  for (let i = 0; i < data.length; i++) { const c = rem[i]!; if (c) for (let j = 1; j < g.length; j++) rem[i + j]! ^= mul(g[j]!, c); }
  return rem.slice(data.length);
}
const capacity = (v: number) => M[v]![1].reduce((s, [b, d]) => s + b * d, 0);

function codewords(data: Uint8Array): [number, number[]] {
  const len = data.length;
  let v = 0;
  for (let t = 1; t <= 10; t++) { const cb = t < 10 ? 8 : 16; if (4 + cb + 8 * len <= 8 * capacity(t)) { v = t; break; } }
  if (!v) throw new Error('qr: too long for versions 1–10');
  const countBits = v < 10 ? 8 : 16;
  const bits: number[] = [0, 1, 0, 0];
  for (let i = countBits - 1; i >= 0; i--) bits.push((len >> i) & 1);
  for (const byte of data) for (let i = 7; i >= 0; i--) bits.push((byte >> i) & 1);
  const cap = 8 * capacity(v);
  for (let i = 0, m = Math.min(4, cap - bits.length); i < m; i++) bits.push(0);
  while (bits.length % 8) bits.push(0);
  const cw: number[] = [];
  for (let i = 0; i < bits.length; i += 8) { let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]!; cw.push(b); }
  const pad = [0xec, 0x11]; let k = 0;
  while (cw.length < capacity(v)) { cw.push(pad[k % 2]!); k++; }
  const [ecN, groups] = M[v]!;
  const blocks: number[][] = []; let pos = 0;
  for (const [nb, nd] of groups) for (let i = 0; i < nb; i++) { blocks.push(cw.slice(pos, pos + nd)); pos += nd; }
  const ecs = blocks.map((b) => rsRemainder(b, ecN));
  const out: number[] = [];
  const max = Math.max(...blocks.map((b) => b.length));
  for (let i = 0; i < max; i++) for (const b of blocks) if (i < b.length) out.push(b[i]!);
  for (let i = 0; i < ecN; i++) for (const e of ecs) out.push(e[i]!);
  return [v, out];
}

const bitlen = (x: number) => { let n = 0; while (x > 0) { n++; x >>= 1; } return n; };
function bch(value: number, poly: number): number {
  const deg = bitlen(poly) - 1;
  let rem = value << deg;
  while (bitlen(rem) > deg) rem ^= poly << (bitlen(rem) - 1 - deg);
  return (value << deg) | rem;
}
function maskBit(k: number, r: number, c: number): boolean {
  switch (k) {
    case 0: return (r + c) % 2 === 0;
    case 1: return r % 2 === 0;
    case 2: return c % 3 === 0;
    case 3: return (r + c) % 3 === 0;
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5: return ((r * c) % 2) + ((r * c) % 3) === 0;
    case 6: return (((r * c) % 2) + ((r * c) % 3)) % 2 === 0;
    default: return (((r + c) % 2) + ((r * c) % 3)) % 2 === 0;
  }
}
type Grid = number[][];
function base(v: number): [Grid, boolean[][]] {
  const n = 17 + 4 * v;
  const m: Grid = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  const fn: boolean[][] = Array.from({ length: n }, () => new Array<boolean>(n).fill(false));
  const put = (r: number, c: number, val: boolean | number) => { if (r >= 0 && r < n && c >= 0 && c < n) { m[r]![c] = val ? 1 : 0; fn[r]![c] = true; } };
  for (const [r0, c0] of [[0, 0], [0, n - 7], [n - 7, 0]] as const) {
    for (let dr = -1; dr < 8; dr++) for (let dc = -1; dc < 8; dc++) {
      const inside = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6;
      const ring = inside && (dr === 0 || dr === 6 || dc === 0 || dc === 6 || (dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4));
      put(r0 + dr, c0 + dc, ring);
    }
  }
  for (let i = 8; i < n - 8; i++) { put(6, i, i % 2 === 0); put(i, 6, i % 2 === 0); }
  const a = ALIGN[v]!; const last = a.length ? a[a.length - 1]! : -1;
  for (const r of a) for (const c of a) {
    if ((r === 6 && c === 6) || (r === 6 && c === last) || (r === last && c === 6)) continue;
    for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++) put(r + dr, c + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
  }
  put(n - 8, 8, 1);
  for (let i = 0; i < 9; i++) { fn[8]![i] = true; fn[i]![8] = true; }
  for (let i = 0; i < 8; i++) { fn[8]![n - 1 - i] = true; fn[n - 1 - i]![8] = true; }
  if (v >= 7) {
    const info = bch(v, 0x1f25);
    for (let i = 0; i < 18; i++) { const bit = (info >> i) & 1; put(Math.floor(i / 3), n - 11 + (i % 3), bit); put(n - 11 + (i % 3), Math.floor(i / 3), bit); }
  }
  return [m, fn];
}
/** the data-module positions in placement order (shared by the encoder and the decoder) */
function dataPositions(fn: boolean[][]): [number, number][] {
  const n = fn.length, out: [number, number][] = [];
  let col = n - 1, up = true;
  while (col > 0) {
    if (col === 6) col--;
    for (let i = 0; i < n; i++) { const r = up ? n - 1 - i : i; for (const c of [col, col - 1]) if (!fn[r]![c]) out.push([r, c]); }
    up = !up; col -= 2;
  }
  return out;
}
function place(m: Grid, fn: boolean[][], cw: number[]) {
  const bits: number[] = []; for (const b of cw) for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
  dataPositions(fn).forEach(([r, c], k) => { m[r]![c] = k < bits.length ? bits[k]! : 0; });
}
const formatInfo = (mask: number) => bch((0b00 << 3) | mask, 0x537) ^ 0x5412; // level M = 00
function format(m: Grid, mask: number) {
  const n = m.length, info = formatInfo(mask), bit = (i: number) => (info >> i) & 1;
  for (let i = 0; i < 6; i++) m[i]![8] = bit(i);
  m[7]![8] = bit(6); m[8]![8] = bit(7); m[8]![7] = bit(8);
  for (let i = 9; i < 15; i++) m[8]![14 - i] = bit(i);
  for (let i = 0; i < 8; i++) m[8]![n - 1 - i] = bit(i);
  for (let i = 8; i < 15; i++) m[n - 15 + i]![8] = bit(i);
  m[n - 8]![8] = 1;
}
function penalty(m: Grid): number {
  const n = m.length; let p = 0;
  const cols: Grid = []; for (let c = 0; c < n; c++) cols.push(m.map((row) => row[c]!));
  for (const grid of [m, cols]) for (const row of grid) {
    let run = 0, prev = -1;
    for (const x of row) { if (x === prev) run++; else { if (run >= 5) p += run - 2; run = 1; prev = x; } }
    if (run >= 5) p += run - 2;
    const s = row.join('');
    p += 40 * ((s.match(/10111010000/g)?.length ?? 0) + (s.match(/00001011101/g)?.length ?? 0));
  }
  for (let r = 0; r < n - 1; r++) for (let c = 0; c < n - 1; c++) { const x = m[r]![c]; if (x === m[r]![c + 1] && x === m[r + 1]![c] && x === m[r + 1]![c + 1]) p += 3; }
  let dark = 0; for (const row of m) for (const x of row) dark += x;
  p += 10 * Math.floor(Math.abs(dark * 20 - n * n * 10) / (n * n));
  return p;
}

/** the module matrix (rows of 0/1) for a text (UTF-8 bytes), level M */
export function encodeQr(text: string): Grid {
  const [v, cw] = codewords(new TextEncoder().encode(text));
  let best: Grid | null = null, bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const [m, fn] = base(v);
    place(m, fn, cw);
    const n = m.length;
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (!fn[r]![c] && maskBit(mask, r, c)) m[r]![c]! ^= 1;
    format(m, mask);
    const score = penalty(m);
    if (score < bestScore) { bestScore = score; best = m; }
  }
  return best!;
}

/** an inline SVG of the code (dark modules as one path, 4-module quiet zone) */
export function qrSvg(text: string, o: { label?: string; color?: string } = {}): string {
  const m = encodeQr(text), q = 4, n = m.length + 2 * q;
  let d = '';
  m.forEach((row, r) => { let c = 0; while (c < row.length) { if (!row[c]) { c++; continue; } const s = c; while (c < row.length && row[c]) c++; d += `M${s + q} ${r + q}h${c - s}v1h-${c - s}z`; } });
  const label = o.label ? ` role="img" aria-label="${o.label.replace(/[&<>"]/g, '')}"` : ' aria-hidden="true"';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges"${label}><rect width="${n}" height="${n}" fill="#fff"/><path d="${d}" fill="${o.color ?? '#000'}"/></svg>`;
}

/** reads back a matrix made by `encodeQr` (no error correction): the text, or throws */
export function decodeQr(m: Grid): string {
  const n = m.length, v = (n - 17) / 4;
  if (!Number.isInteger(v) || v < 1 || v > 10) throw new Error('qr: size');
  let info = 0; // first copy of the format bits, as written by format()
  const read = (i: number) => (i < 6 ? m[i]![8]! : i === 6 ? m[7]![8]! : i === 7 ? m[8]![8]! : i === 8 ? m[8]![7]! : m[8]![14 - i]!);
  for (let i = 0; i < 15; i++) info |= read(i) << i;
  const mask = [0, 1, 2, 3, 4, 5, 6, 7].find((k) => formatInfo(k) === info);
  if (mask === undefined) throw new Error('qr: format');
  const [, fn] = base(v);
  const bits = dataPositions(fn).map(([r, c]) => m[r]![c]! ^ (maskBit(mask, r, c) ? 1 : 0));
  const all: number[] = [];
  for (let i = 0; i + 8 <= bits.length; i += 8) { let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]!; all.push(b); }
  // de-interleave the data codewords
  const [, groups] = M[v]!;
  const sizes: number[] = []; for (const [nb, nd] of groups) for (let i = 0; i < nb; i++) sizes.push(nd);
  const blocks = sizes.map(() => [] as number[]);
  let k = 0;
  for (let i = 0; i < Math.max(...sizes); i++) sizes.forEach((s, bi) => { if (i < s) blocks[bi]!.push(all[k++]!); });
  const data = blocks.flat();
  const dbits: number[] = []; for (const b of data) for (let i = 7; i >= 0; i--) dbits.push((b >> i) & 1);
  const num = (from: number, len: number) => { let x = 0; for (let i = 0; i < len; i++) x = (x << 1) | dbits[from + i]!; return x; };
  if (num(0, 4) !== 0b0100) throw new Error('qr: not byte mode');
  const cb = v < 10 ? 8 : 16, len = num(4, cb);
  const bytes = new Uint8Array(len); for (let i = 0; i < len; i++) bytes[i] = num(4 + cb + 8 * i, 8);
  return new TextDecoder().decode(bytes);
}
